import React, { forwardRef, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import {
  ASPECT_RATIO_VALUES,
  DEFAULT_VIDEO_BACKGROUND_COLOR,
} from '@src/constants/dubbing';
import {
  StyledVideoPreviewPanel,
  StyledScrollArea,
  StyledFunctionBar,
  StyledTabSection,
  StyledToggleButton,
  StyledIconOnlyButton,
} from './index.style';
import AspectRatioDropdown from './AspectRatioDropdown';
import BackgroundColorPicker from './BackgroundColorPicker';
import VideoStage from './VideoStage';

const VideoPreviewPanel = forwardRef(
  (
    {
      videoUrl,
      isExpanded,
      onToggleExpand,
      aspectRatioEnabled = false,
      bgColorEnabled = false,
      aspectRatio = ASPECT_RATIO_VALUES.ORIGINAL,
      onAspectRatioChange,
      backgroundColor = DEFAULT_VIDEO_BACKGROUND_COLOR,
      onBackgroundColorChange,
      // Frozen while the mp4 render runs: the file being built already baked in
      // the current settings, so changing them mid-render would be a lie.
      settingsDisabled = false,
      // Playhead past the last frame of the video: the voice or the bgm
      // runs longer, so the preview shows the project's background colour for
      // that stretch instead of the video's frozen final frame.
      showBackgroundOnly = false,
      // Feature flag `video-dubbing-caption`: the caption layer; the three props
      // below only give it a clock.
      captionOverlayEnabled = false,
      isPlaying = false,
      getCurrentSec,
      currentSec = 0,
    },
    videoRef,
  ) => {
    const { t } = useTranslation();
    const videoVolume = useSelector((state) => state.project.volumes?.media);

    useEffect(() => {
      const video = videoRef?.current;
      if (!video) return;
      video.volume = Math.max(0, Math.min(1, videoVolume ?? 1));
    }, [videoVolume, videoRef, videoUrl]);

    // Ratio state stays here (as before the stage extraction) and is shared
    // by whichever stage renders. Cheap to host in the parent: it only
    // changes on video metadata load or a settings change, never per
    // playback tick.
    const aspectRatioCssValue = useMemo(() => {
      if (!aspectRatioEnabled) return null;
      if (aspectRatio === ASPECT_RATIO_VALUES.ORIGINAL) return null;
      const [w, h] = aspectRatio.split(':');
      const width = Number(w);
      const height = Number(h);
      if (!width || !height) return null;
      return `${width} / ${height}`;
    }, [aspectRatio, aspectRatioEnabled]);

    const selectedRatio = useMemo(() => {
      if (!aspectRatioCssValue) return null;
      const [w, h] = aspectRatio.split(':').map(Number);
      return w / h;
    }, [aspectRatio, aspectRatioCssValue]);

    const [videoNaturalRatio, setVideoNaturalRatio] = useState(null);
    const handleVideoMetadata = (event) => {
      const v = event.currentTarget;
      if (v.videoWidth && v.videoHeight) {
        setVideoNaturalRatio(v.videoWidth / v.videoHeight);
      }
    };

    const expandLabel = isExpanded ? t('collapse') : t('expand');
    const ExpandIcon = isExpanded
      ? CloseFullscreenRoundedIcon
      : OpenInFullRoundedIcon;

    return (
      <StyledVideoPreviewPanel>
        <StyledScrollArea>
          <StyledFunctionBar>
            {aspectRatioEnabled || bgColorEnabled ? (
              <>
                {aspectRatioEnabled && (
                  <StyledTabSection hasDividerRight>
                    <AspectRatioDropdown
                      value={aspectRatio}
                      onChange={onAspectRatioChange}
                      disabled={settingsDisabled}
                    />
                  </StyledTabSection>
                )}
                {bgColorEnabled && (
                  <StyledTabSection hasDividerRight>
                    <BackgroundColorPicker
                      value={backgroundColor}
                      onChange={onBackgroundColorChange}
                      disabled={settingsDisabled}
                    />
                  </StyledTabSection>
                )}
                <StyledTabSection>
                  <StyledIconOnlyButton
                    onClick={onToggleExpand}
                    type="button"
                    aria-label={expandLabel}
                  >
                    <ExpandIcon className="toggle-icon" />
                  </StyledIconOnlyButton>
                </StyledTabSection>
              </>
            ) : (
              <StyledTabSection>
                <StyledToggleButton
                  isActive={!isExpanded}
                  onClick={onToggleExpand}
                  type="button"
                  aria-label={expandLabel}
                >
                  <ExpandIcon className="toggle-icon" />
                  {expandLabel}
                </StyledToggleButton>
              </StyledTabSection>
            )}
          </StyledFunctionBar>

          <VideoStage
            ref={videoRef}
            videoUrl={videoUrl}
            selectedRatio={selectedRatio}
            aspectRatioCssValue={aspectRatioCssValue}
            videoNaturalRatio={videoNaturalRatio}
            onVideoMetadata={handleVideoMetadata}
            backgroundColor={backgroundColor}
            showBackgroundOnly={showBackgroundOnly}
            captionOverlayEnabled={captionOverlayEnabled}
            isPlaying={isPlaying}
            getCurrentSec={getCurrentSec}
            currentSec={currentSec}
          />
        </StyledScrollArea>
      </StyledVideoPreviewPanel>
    );
  },
);

export default memo(VideoPreviewPanel);
